import { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Title,
  TextInput,
  Button,
  Text,
  Checkbox,
  Flex,
} from "@mantine/core";
import { IconShieldLock } from "@tabler/icons-react";
import { Link, useNavigate } from "react-router-dom";
import trpc, { saveToken } from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { useUser } from "@/context/UserContext";
import {
  TWO_FACTOR_DEVICE_TOKEN_KEY,
  TWO_FACTOR_RETURN_PATH_KEY,
  TWO_FACTOR_TEMP_TOKEN_KEY,
} from "../constants";

/**
 * Second step after password login when TOTP is enabled.
 * Expects a short-lived token in sessionStorage from the login mutation.
 */
export default function LoginTwoFactorPage() {
  const navigate = useNavigate();
  const { refetchUser } = useUser();
  const [otp, setOtp] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = sessionStorage.getItem(TWO_FACTOR_TEMP_TOKEN_KEY);
    if (!t) {
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const otpOk = /^\d{6}$/.test(otp.trim()) || /^\d{8}$/.test(otp.trim());

  const submit = async () => {
    const tempToken = sessionStorage.getItem(TWO_FACTOR_TEMP_TOKEN_KEY);
    if (!tempToken || !otpOk) return;
    setLoading(true);
    try {
      const data = await trpc.twoFactor.loginVerify.mutate({
        tempToken,
        otp: otp.trim(),
        trustDevice: trustDevice || undefined,
      });
      if (data.deviceToken) {
        localStorage.setItem(TWO_FACTOR_DEVICE_TOKEN_KEY, data.deviceToken);
      }
      saveToken(data.token);
      refetchUser();
      const path = sessionStorage.getItem(TWO_FACTOR_RETURN_PATH_KEY) ?? "/dashboard";
      sessionStorage.removeItem(TWO_FACTOR_TEMP_TOKEN_KEY);
      sessionStorage.removeItem(TWO_FACTOR_RETURN_PATH_KEY);
      successNotification({ title: "Signed in", message: "Two-factor verification successful." });
      navigate(path, { replace: true });
    } catch (e) {
      handleTrpcError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <Paper p="xl" radius="lg" maw={400} w="100%" bg="#f7f9ff" style={{ border: "1px solid #04305c" }}>
        <Flex align="center" gap={8} mb="md">
          <IconShieldLock size={22} color="var(--mantine-color-blue-7)" />
          <Title order={3}>Two-factor authentication</Title>
        </Flex>
        <Text size="sm" c="dimmed" mb="lg">
          Enter the 6-digit code from your authenticator app, or an 8-digit backup code.
        </Text>
        <TextInput
          label="Authentication code"
          placeholder="000000"
          value={otp}
          onChange={(e) => setOtp(e.currentTarget.value.replace(/\D/g, "").slice(0, 8))}
          maxLength={8}
          mb="md"
          autoComplete="one-time-code"
        />
        <Checkbox
          label="Trust this device for 7 days"
          checked={trustDevice}
          onChange={(e) => setTrustDevice(e.currentTarget.checked)}
          mb="lg"
        />
        <Button fullWidth onClick={submit} loading={loading} disabled={!otpOk || loading}>
          Verify and continue
        </Button>
        <Flex justify="center" mt="md">
          <Link to="/login" style={{ fontSize: 14 }}>
            ← Back to sign in
          </Link>
        </Flex>
      </Paper>
    </Box>
  );
}
